import { useRef, useState } from 'react';
import { IMPORT_MAX_BYTES, toCsv, type ImportDomain, type ImportResult } from '@forecastify/shared';
import { Button, Dialog, Icon, Select } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { saveCsv } from '../exports/export-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The import wizard — `markup.html` 2768–2850. C17.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Four stages, from the mockup's five. Drop → working → done, or → error.
 *
 * ⚠️⚠️ **THE MOCKUP'S FIFTH STAGE — `background` — IS NOT BUILT, AND THE REASON IS THAT ITS OWN
 * PROMISE IS UNKEEPABLE HERE.** It says *"N sor sok — nem kell megvárnod, e-mailben szólunk, ha
 * kész"*, which needs the file to survive the request: uploaded somewhere, then read by a worker.
 * There is no object storage (`config.toml` disables it), and putting a five-megabyte CSV in a
 * `jsonb` job payload to be re-parsed later is a design decision, not a stage. Instead the server
 * **refuses** past `IMPORT_SYNC_LIMIT` and says how many rows it saw, so somebody can split the file
 * — which is worse UX and an honest answer. Recorded in `chunks.md`.
 *
 * ⚠️ **There is no progress bar at all** — see the working stage for why.
 */

type Stage = 'drop' | 'working' | 'done' | 'error';

export function importTitle(t: Translate, domain: ImportDomain): string {
  return domain === 'task' ? t('import.title.task') : t('import.title.worklog');
}

export interface ImportDialogProps {
  /** `null` closes it. */
  readonly domain: ImportDomain | null;
  readonly result: ImportResult | null;
  /** A refusal that is about the file as a whole — a bad header, or too many rows. */
  readonly fileError: string | null;
  readonly working: boolean;
  readonly onClose: () => void;
  readonly onUpload: (filename: string, csv: string) => void;
  readonly onSample: () => void;
  readonly onReset: () => void;
  /** P2-32.2 · for a task import: the line item rows with no `sortetel` column land on. */
  readonly lineItems?: readonly { readonly id: string; readonly label: string }[];
  readonly defaultLineItemId?: string;
  readonly onDefaultLineItem?: (id: string) => void;
}

export function ImportDialog(props: ImportDialogProps) {
  const t = useT();
  const { domain, result } = props;
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [tooBig, setTooBig] = useState(false);

  const stage: Stage = props.working
    ? 'working'
    : props.fileError !== null
      ? 'error'
      : result === null
        ? 'drop'
        : 'done';

  if (domain === null) return null;

  const take = (file: File | undefined): void => {
    if (file === undefined) return;
    /**
     * ⚠️ **Checked here as well as on the server.** The server's limit is the one that matters, but a
     * ten-megabyte file would be read into memory, base64'd through JSON and refused — so the browser
     * declines first and says the size, which is the difference between a wait and an answer.
     */
    if (file.size > IMPORT_MAX_BYTES) {
      setTooBig(true);
      return;
    }
    setTooBig(false);
    void file.text().then((text) => {
      props.onUpload(file.name, text);
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      /* ⚠️ Item 28 · no `description` — see `export-dialog.tsx`'s own note. */
      title={importTitle(t, domain)}
      closeLabel={t('common.close')}
      width={560}
    >
      {stage === 'drop' && (
        <div className="flex flex-col gap-3">
          {/* ⚠️ P2-32.2 · every new task needs a line item: a row's own `sortetel` column, or this.
              Optional, because a file that names one on every row needs no default. */}
          {domain === 'task' && props.onDefaultLineItem !== undefined ? (
            <label className="flex flex-col gap-1 text-[12px]">
              <span className="text-muted-foreground">{t('import.defaultLineItem')}</span>
              <Select
                aria-label={t('import.defaultLineItem')}
                searchable
                searchPlaceholder={t('rule.searchValues')}
                value={props.defaultLineItemId ?? ''}
                onChange={(event) => {
                  props.onDefaultLineItem?.(event.target.value);
                }}
                options={[
                  { value: '', label: t('import.defaultLineItemNone') },
                  ...(props.lineItems ?? []).map((item) => ({ value: item.id, label: item.label })),
                ]}
              />
            </label>
          ) : null}
          {/**
           * ⚠️ **A `<label>` around a hidden file input, which is the mockup's own construction** — and
           * the only one that is both a drop target and keyboard-reachable. A `<div onClick>` calling
           * `input.click()` is not focusable and not announced.
           */}
          <label
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setOver(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setOver(false);
              take(event.dataTransfer.files[0]);
            }}
            className={[
              'flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed px-6 py-10 text-center transition-colors',
              over ? 'border-primary bg-primary/[0.06]' : 'border-input hover:bg-accent/40',
            ].join(' ')}
          >
            <span className="bg-muted text-muted-foreground grid size-12 place-items-center rounded-xl">
              <Icon name="upload" size={22} />
            </span>
            <span className="text-[13px] font-medium">{t('import.drop')}</span>
            <span className="text-muted-foreground text-[11.5px]">
              {t('import.dropNote', { mb: String(Math.round(IMPORT_MAX_BYTES / 1_000_000)) })}
            </span>
            <input
              ref={input}
              type="file"
              /* ⚠️ `.csv` only — the mockup offers `.xlsx` too, and nothing here can read one. An
                 accept list that lets a file through the picker and then refuses it is worse. */
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => {
                take(event.target.files?.[0]);
                /* ⚠️ Cleared, so choosing the SAME file twice fires `change` again — otherwise a
                   retry after a failure appears to do nothing. */
                event.target.value = '';
              }}
            />
          </label>

          {tooBig && (
            <p role="alert" className="text-destructive text-[12px]">
              {t('import.tooBig', { mb: String(Math.round(IMPORT_MAX_BYTES / 1_000_000)) })}
            </p>
          )}

          {/* ⚠️⚠️ **THE COLUMN LIST IS GONE — item 28's *"footer info removed"*.** It printed
              *"key, title, project, estimate, start, due"* beside the sample button, which is the
              same information the SAMPLE FILE carries in a form somebody can actually use. A person
              who needs the columns downloads the sample; a person who does not was reading a
              comma-separated string under a drop zone. */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={props.onSample}>
              <Icon name="download" size={14} />
              {t('import.sample')}
            </Button>
          </div>
        </div>
      )}

      {stage === 'working' && (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          {/**
           * ⚠️⚠️ **NO PROGRESS BAR, AND THAT IS DELIBERATE TWICE OVER.** The mockup animates a fake one
           * at 16% per tick over a `setInterval`; there is nothing real to report until the request
           * answers, and a bar that crawls to 90% and waits is the specific lie people learn to
           * distrust. `Progress` also takes a number and has no indeterminate state — adding one would
           * be a design-system change to support a bar that should not exist.
           */}
          <Icon name="clock" size={22} className="text-muted-foreground" />
          <p className="text-[12.5px] font-medium">{t('import.working')}</p>
          <p className="text-muted-foreground max-w-[360px] text-[11.5px] leading-relaxed">
            {t('import.workingNote')}
          </p>
        </div>
      )}

      {stage === 'error' && (
        <div className="flex flex-col gap-3">
          <div className="border-destructive/40 bg-destructive/5 flex items-start gap-3 rounded-lg border p-3">
            <Icon name="triangle-alert" size={16} className="text-destructive mt-0.5 shrink-0" />
            <p className="text-[12.5px] leading-relaxed">{props.fileError}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1" />
            <Button variant="outline" onClick={props.onReset}>
              {t('import.newFile')}
            </Button>
          </div>
        </div>
      )}

      {stage === 'done' && result !== null && (
        <Report result={result} onReset={props.onReset} onClose={props.onClose} />
      )}
    </Dialog>
  );
}

/**
 * What happened.
 *
 * ⚠️⚠️ **FOUR NUMBERS, NOT ONE.** Created, updated, skipped and failed are different outcomes and the
 * mockup reports them separately for a reason: *"N sor kimaradt, mert ugyanaz a bejegyzés már
 * szerepelt"* is a success, and folding it into a single "imported" count would make a second run of
 * the same file look like it did nothing wrong — or like it did everything wrong.
 */
function Report({
  result,
  onReset,
  onClose,
}: {
  readonly result: ImportResult;
  readonly onReset: () => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const anyFailed = result.failed > 0;

  return (
    <div className="flex flex-col gap-3">
      <div
        className={[
          'flex items-start gap-3 rounded-lg border p-3',
          anyFailed ? 'border-amber-500/40 bg-amber-500/10' : 'border-primary/40 bg-primary/5',
        ].join(' ')}
      >
        <Icon
          name={anyFailed ? 'triangle-alert' : 'circle-check'}
          size={16}
          className={anyFailed ? 'mt-0.5 shrink-0 text-amber-600' : 'text-primary mt-0.5 shrink-0'}
        />
        <div className="min-w-0">
          <div className="text-[12.5px] font-medium">
            {anyFailed
              ? t('import.partial', {
                  ok: String(result.created + result.updated),
                  bad: String(result.failed),
                })
              : t('import.done', { ok: String(result.created + result.updated) })}
          </div>
          <div className="text-muted-foreground mt-0.5 text-[11.5px] leading-relaxed">
            {t('import.tally', {
              created: String(result.created),
              updated: String(result.updated),
              skipped: String(result.skipped),
            })}
          </div>
        </div>
      </div>

      {result.errors.length > 0 && (
        <>
          <ul className="max-h-[180px] overflow-y-auto rounded-lg border">
            {result.errors.map((error) => (
              <li
                key={`${String(error.line)}-${error.message}`}
                className="flex items-start gap-2.5 border-b px-3 py-2 text-[12px] last:border-0"
              >
                {/* ⚠️ The line number in the FILE, so it means the row somebody sees in Excel. */}
                <span className="text-muted-foreground w-[64px] shrink-0 tabular-nums">
                  {t('import.line', { line: String(error.line) })}
                </span>
                <span className="min-w-0 flex-1">{error.message}</span>
              </li>
            ))}
          </ul>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              /**
               * ⚠️ **Built from the same list the dialog shows, with the shared writer.** The mockup
               * hand-joins `row + ';' + msg`, which breaks the moment a message contains a semicolon
               * — and one of ours contains a project prefix somebody chose.
               */
              saveCsv(
                'import-hibak.csv',
                toCsv(
                  [t('import.csv.line'), t('import.csv.error')],
                  result.errors.map((error) => [error.line, error.message]),
                ),
              );
            }}
          >
            <Icon name="download" size={14} />
            {t('import.downloadErrors')}
          </Button>
        </>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1" />
        <Button variant="outline" onClick={onReset}>
          {t('import.newFile')}
        </Button>
        <Button onClick={onClose}>{t('common.done')}</Button>
      </div>
    </div>
  );
}
